import type { ReactNode } from 'react'
import { Base, ReceiptIcon, useTheme } from '@itsrever/design-system'
import { Trans, useTranslation } from 'react-i18next'
import { formatCurrency } from '@/utils'
import { CostItem, OrderedItem, ReturnedItem } from '@/domain/returns'
import {
  Compensation,
  RefundMethod,
  CompensationOptions,
  sumCompensation
} from '@/domain/refund'
import moment from 'moment'
import { useAppSelector } from '@/redux/hooks'

function findUserSelectedCompensation(
  userSelectedPM: RefundMethod,
  compensation: Compensation
): CompensationOptions {
  switch (userSelectedPM) {
    case 'BANK_TRANSFER':
      return compensation.bankTransfer
    case 'ORIGINAL_PM':
      return compensation.originalPM
    case 'PROMO_CODE':
      return compensation.promoCode
    case 'GIFT_CARD':
      return compensation.giftCard
    default:
      return {} as CompensationOptions
  }
}

interface RefundSummaryProps {
  returnedItems: ReturnedItem[]
  exchangeItems: OrderedItem[]
  extraLineItems: CostItem[]
  compensation: Compensation
  userSelectedPM: RefundMethod
  children?: ReactNode
}

const RefundSummary: React.FC<RefundSummaryProps> = ({
  returnedItems,
  exchangeItems,
  extraLineItems,
  compensation,
  userSelectedPM,
  children
}) => {
  const {
    t,
    i18n: { language }
  } = useTranslation()
  const ecommerceName =
    useAppSelector((store) => store.settingsApi.settings.response.name) ?? ''

  const theme = useTheme()

  const getCostColor = (cost: number) => {
    if (cost < 0) {
      return theme.colors.error.main
    }
    return theme.colors.common.black
  }

  const totalAdjustments = sumCompensation(compensation, 'adjustments')
  const totalAdjustedCommitments = sumCompensation(
    compensation,
    'adjustedCommitment'
  )

  const processCompensation = findUserSelectedCompensation(
    userSelectedPM,
    compensation
  )

  const formatUser = (user: string) => {
    if (user.includes('itsrever')) return 'REVER'
    if (user === 'checkout') return t('summary.paid_at_checkout')
    return user
  }

  const adjustments_list = processCompensation.adjustments_list

  return (
    <div>
      <div className="mb-3 flex flex-wrap justify-between gap-3">
        <div className="flex items-center gap-1.5">
          <ReceiptIcon className="text-primary-main" />
          <p className="text-sm font-semibold">{t('summary.summary_title')}</p>
        </div>
        {children}
      </div>

      <div className="flex justify-between gap-2">
        <p>{t('summary.returned_items')}</p>
      </div>
      {returnedItems.map((item, i) => {
        return (
          <div className="flex items-center justify-between gap-1" key={i}>
            <p>
              {item.quantity}&times; {item.product.productName}:
            </p>
            <p
              className="whitespace-nowrap"
              style={{
                color: getCostColor(Number(item.total.amount))
              }}
            >
              {formatCurrency(item.total.amount, item.total.currency)}
            </p>
          </div>
        )
      })}

      {exchangeItems.length > 0 ? (
        <>
          <div className="mt-4 flex items-center gap-1">
            <p>{t('summary.new_items')}</p>
          </div>
          {exchangeItems.map((item, i) => {
            return (
              <div className="flex items-center justify-between gap-1" key={i}>
                <div>
                  <p>
                    {item.quantity}&times; {item.productName}:
                  </p>
                  <p className="text-xs text-grey-2">{item.variantName}</p>
                </div>
                <p
                  className="whitespace-nowrap"
                  style={{
                    color: getCostColor(Number(item.total.amount))
                  }}
                >
                  {formatCurrency(item.total.amount, item.total.currency)}
                </p>
              </div>
            )
          })}
        </>
      ) : null}

      {extraLineItems.length > 0 ? (
        <>
          <div className="mt-4" />
          {extraLineItems.map((item, i) => {
            return (
              <div className="flex items-center justify-between gap-1" key={i}>
                <div className="flex items-center gap-1">
                  <p>{t(`cost_type.${item.type}`, { ecommerceName })}</p>{' '}
                  {item.type === 'INSTANT_EXCHANGE' ? (
                    <Base.InfoTooltip
                      label={t('summary.about_instant_exchange')}
                      content={t('summary.instant_exchange_description')}
                    />
                  ) : null}
                  :
                </div>
                <p
                  className="whitespace-nowrap"
                  style={{
                    color: getCostColor(Number(item.total.amount))
                  }}
                >
                  {formatCurrency(item.total.amount, item.total.currency)}
                </p>
              </div>
            )
          })}
        </>
      ) : null}

      <hr className="my-2" />
      <div className="flex items-center justify-between gap-1">
        <p className="font-bold">{t('summary.total')}</p>
        <p
          className="whitespace-nowrap font-bold"
          style={{
            color:
              Number(processCompensation.initialCommitment.amount) >= 0
                ? theme.colors.success.main
                : theme.colors.error.main
          }}
        >
          {formatCurrency(
            processCompensation.initialCommitment.amount,
            processCompensation.initialCommitment.currency
          )}
        </p>
      </div>

      {Number(totalAdjustments.amount) !== 0 ? (
        <div>
          <dl className="space-y-3 py-2.5">
            {adjustments_list?.map(({ amount, reason, user, created_at }) =>
              amount?.presentment_money?.amount &&
              amount?.presentment_money?.currency_code ? (
                <div>
                  <div className="flex justify-between gap-4 text-sm">
                    <dt className="font-semibold">{t('summary.adjustment')}</dt>
                    <dd
                      className="font-semibold"
                      style={{
                        color: getCostColor(
                          Number(amount?.presentment_money?.amount)
                        )
                      }}
                    >
                      {formatCurrency(
                        amount?.presentment_money?.amount,
                        amount?.presentment_money?.currency_code
                      )}
                    </dd>
                  </div>
                  <div className="space-y-1 pt-1">
                    {reason ? (
                      <p className="text-xs font-normal">{reason}</p>
                    ) : null}
                    <div>
                      <p className="text-xs font-normal">
                        <Trans
                          i18nKey="summary.edited_by"
                          values={{
                            user: user ? formatUser(user) : '-',
                            created_at: moment(created_at)
                              .locale(language)
                              .format('MMMM Do, YYYY')
                          }}
                          components={{
                            span: <span className="font-medium" />
                          }}
                        />
                      </p>
                    </div>
                  </div>
                </div>
              ) : null
            )}
          </dl>
          <hr className="my-2" />
          <div className="flex items-center justify-between gap-1">
            <p className="font-bold">{t('summary.final_balance')}</p>
            <p
              className="whitespace-nowrap font-bold"
              style={{
                color:
                  Number(processCompensation.adjustedCommitment.amount) >= 0
                    ? theme.colors.success.main
                    : theme.colors.error.main
              }}
            >
              {formatCurrency(
                totalAdjustedCommitments.amount,
                totalAdjustedCommitments.currency
              )}
            </p>
          </div>
        </div>
      ) : null}
    </div>
  )
}

export default RefundSummary
